<template>
  <div id="projects" ref="mainDiv" @scroll="onScroll" class="wrapperProjectsMain">
    <div v-if="country && population && projectsNb" class="projects-container">
      <div>
        <projects-stats-header
          :countryName="country.name"
          :peopleImpacted="population"
          :projectsCompleted="projectsNb"
          :completed="true" />
      </div>
      <section v-if="currentProjects.length > 0" class="projects-wrapper">
        <div
          v-for="(project, index) in currentProjects"
          :key="project.id + 1"
          :id="'project' + index"
          class="project">
          <project-header
            :project="project"
            :showAnalytics="true"
            :has_many="true"
            :completed="true" />
          <project-carousel
            v-if="project.pictures?.length > 0"
            :pictures="project.pictures"
            :stages="stages"
            :completed="true"
            :projectIndex="index"
            :ref="`carousel${index}`"
            :has_many="false"
            :inTheViewPort="carouselVisibility[index]" />
        </div>
        <loading v-if="loading" />
      </section>
    </div>
  </div>
</template>

<script>

  import axios from 'axios'
  import projectHeader from '../reusableComponents/ProjectHeader.vue'
  import projectCarousel from '../reusableComponents/ProjectCarousel.vue'
  import loading from '../reusableComponents/Loading.vue'
  import projectsStatsHeader from '../reusableComponents/ProjectsStatsHeader.vue'
  import './stylesheets/main.scss'

  export default {
    props: {
      country: { type: Object, required: true },
      projectsNb: { type: Number, required: true },
      population: { type: Number, required: true }
    },
    components: {
      projectHeader,
      projectCarousel,
      loading,
      projectsStatsHeader
    },
    data() {
      return {
        index: 0,
        addMore: 5,
        currentProjects: [],
        projectTypes: null,
        pictures: null,
        loading: false,
        mainFuncRunning: false,
        noMoreProjects: false,
        stages: null,
        carouselVisibility: [],
      }
    },
    async created() {
      if (this.country.id != 3) {
        this.addMore = 20;
      }
      let projectsTypesFetch = await axios.post('/api/project_types', {
        index: 0,
        totalCount: 50,
        additionalColumns: ['id', 'name', 'icon', 'marker_icon'],
      })
      this.projectTypes = projectsTypesFetch.data.data
      let projectsStagesFetch = await axios.post('/api/project_stages', {
        index: 0,
        totalCount: 50,
        additionalColumns: ['id', 'name', 'picture_caption'],
      })
      this.stages = projectsStagesFetch.data.data
      await this.getMoreProjects()
    },
    mounted() {
      this.postMessageInterval = setInterval(() => {
        parent.postMessage({ newHeight: document.getElementById('projects').scrollHeight }, '*');
      }, 500);

      window.addEventListener('message', (event) => {
        if (event.data.getMoreProjects)
          this.getMoreProjects();
      });
    },
    methods: {
      async getMoreProjects() {
        if (this.mainFuncRunning || this.noMoreProjects)
          return;

        this.mainFuncRunning = true
        let fetchProjects = await axios.post('/api/projects', {
          meta: true,
          index: this.index,
          totalCount: this.addMore,
          additionalColumns: [
            'id',
            'name',
            'project_stage_id',
            'country_id',
            'project_type_id',
            'latitude',
            'longitude',
            'completion_date',
            'description',
            'population',
            'donors',
            'position',
            'monitored',
          ],
          query: {
            country_id_eq: this.country.id,
            project_stage_is_completed_eq: true,
            position_not_null: true,
          },
          sort: 'COALESCE(projects.position, 99999999) ASC, projects.completion_date DESC',
        })

        let idArray = fetchProjects.data.data.map((obj) => obj.id)

        let allPictures = []
        let nbPicturesToDownload = 0
        if (idArray.length > 0) {
          do {
            let getMeta = allPictures.length === 0
            let fetchPictures = await axios.post('/api/pictures', {
              meta: getMeta,
              index: allPictures.length,
              query: {
                project_id_in: idArray,
                share_with_donors_eq: true,
                s: ['cover DESC', 'project_stage_position DESC', 'id DESC'],
              },
              additionalColumns: [
                'source',
                'public_id',
                'project_id',
                'project_stage_id',
                'secure_url',
                'aspect_ratio',
                'height',
                'width',
                'favorite',
              ],
            })
            nbPicturesToDownload = getMeta
              ? fetchPictures.data.meta.recordsFiltered
              : nbPicturesToDownload
            allPictures = allPictures.concat(fetchPictures.data.data)
          } while (allPictures.length < nbPicturesToDownload)
        }

        let organizeProjects = await Promise.all(
          fetchProjects.data.data.map(async (project) => {
            let x = await this.organizeData(project, allPictures)
            return x
          })
        )
        if (this.country.id != 3) {
          this.noMoreProjects = true;
        }
        if (organizeProjects.length === 0) {
          this.noMoreProjects = true
        }

        this.currentProjects = [
          ...this.currentProjects,
          ...organizeProjects.filter(
            (project) =>
              !this.currentProjects.some(
                (currProject) => currProject.id === project.id
              )
          ),
        ]
        this.index += this.addMore
        this.loading = false
        this.mainFuncRunning = false
      },
      async organizeData(project, pictures) {
        let currentType = this.projectTypes.find(
          (projectType) => project.project_type_id === projectType.id
        )
        return {
          id: project.id,
          project_name: project.name,
          completion_date: project.completion_date,
          description: project.description || '',
          location: {
            lat: parseFloat(project.latitude),
            lng: parseFloat(project.longitude),
          },
          project_type: {
            name: currentType.name,
            icon: currentType.icon,
            marker_icon: currentType.marker_icon,
          },
          project_country: {
            name: this.country.name,
            id: this.country.id,
            flag_url: this.country.flag_url,
          },
          pictures: pictures.filter((obj) => obj.project_id === project.id),
          population: project.population,
          donors: project.donors,
          monitored: project.monitored,
        }
      },
      async onScroll(event) {
        this.checkCarouselVisibility()

        const projectsWrapper = event.target

        const scrollThreshold = 600

        if (
          projectsWrapper.scrollTop + projectsWrapper.clientHeight >=
          projectsWrapper.scrollHeight - scrollThreshold
        ) {
          if (this.loading === true || this.mainFuncRunning === true) {
            return
          } else {
            if (this.noMoreProjects) {
              clearInterval(this.postMessageInterval);
              parent.postMessage({ totalHeight: document.getElementById('projects').scrollHeight }, '*');
              return;
            }
            this.loading = true
            await this.getMoreProjects()
          }
        }
      },
      toggleDropdown() {
        document.getElementById('dropdown-header').classList.toggle('show')
      },
      checkCarouselVisibility() {
        this.currentProjects.forEach((project, index) => {
          const refName = `carousel${index}`
          const carouselContainer = this.$refs[refName]

          if (carouselContainer) {
            const navigationName = `slide${index}`
            const element = carouselContainer[0].$refs[navigationName]
            const elementRect = element.getBoundingClientRect()
            const mainDivRect = document.getElementById('projects').getBoundingClientRect()
            const isVisible =
              Math.abs(elementRect.top) >= mainDivRect.top &&
              Math.abs(elementRect.bottom) <= mainDivRect.bottom
            this.carouselVisibility[index] = isVisible
          }
        })
      }
    }
  }

</script>
